// Verbatim-quote resolution - the "mareh makom rescue".
//
// Sefaria's citation linker resolves NAMED citations but misses verses a
// commentary embeds verbatim without naming them - exactly the gap that makes a
// discursive commentary (Abarbanel, Akeidat Yitzchak, Kli Yakar...) look like a
// dead-end even though it quotes pesukim throughout. This recovers those quotes
// HONESTLY: pull candidate quoted Hebrew spans, resolve each via the search
// index, and assert a link ONLY when the candidate source's OWN text contains the
// span verbatim (exact, pointing-insensitive). Search rank is never trusted
// alone - containment is the proof, so a wrong link is impossible by construction.
// Precision over recall: a missed quote is a non-link; a fabricated mareh makom
// is forbidden (ACCURACY-LOCK).

import { stripHebrew } from "./citations.ts";

// Classical quote-introduction formulae (pointing already stripped). A quoted
// pasuk usually follows one of these. Extraction precision only affects COST - a
// bad candidate just fails verification downstream and is dropped; it can never
// become a wrong link. So this list is allowed to be permissive.
const CUES = [
  "שנאמר", "ונאמר", "שכתוב", "ככתוב", "וכתוב", "הכתוב", "כתיב",
  "דכתיב", "כדכתיב", "וכדכתיב", "כמש", "וכמש", "כאמרו",
  "באמרו", "אמרו", "כדאיתא", "דאיתא", "כמאמר", "ואמר", "אומרו",
  "שאמרו", "כדתנן", "תנן", "שכתב", "וכמאמר", "כדכתב", "כדאמרינן",
];
const CUE_SET = new Set(CUES);

const MIN_SPAN_TOKENS = 4; // a 4+ word verbatim run is distinctive enough to pin
const SPAN_TOKENS = 9; // tokens grabbed after a cue (verification trims to fit)
const SEARCH_TOKENS = 6; // the clean CORE we query on (later tokens are often the
//                          commentary's own words, which only pollute the search)
const MIN_SPAN_CHARS = 12; // floor on the verified phrase length (precision guard)

// Collapse matres lectionis (vav / yod): Hebrew commentaries quote in ktiv male
// (full spelling - והשורף, אותם) while the Tanakh text is ktiv chaser (defective
// - והשרף, אתם). Dropping optional vav/yod on BOTH sides lets the verbatim quote
// match its source across that orthographic gap. The length + contiguity floor
// keeps this from creating coincidental matches.
function ktiv(s: string): string {
  return s.replace(/[וי]/g, "");
}

// Candidate quoted phrases (pointing-stripped, space-joined) pulled from a
// source's Hebrew - the run of words just past each citation cue. Deduped; the
// caller caps how many we actually spend a search on.
export function extractQuotedSpans(he: string): string[] {
  const tokens = stripHebrew(he).split(" ").filter(Boolean);
  const out: string[] = [];
  const seen = new Set<string>();
  for (let i = 0; i < tokens.length; i++) {
    const t = tokens[i];
    // Cues attach a conjunctive vav in running text (ו-שנאמר, ו-דכתיב); accept both.
    const bare = t.length > 1 && t.startsWith("ו") ? t.slice(1) : t;
    if (!CUE_SET.has(t) && !CUE_SET.has(bare)) continue;
    const span = tokens.slice(i + 1, i + 1 + SPAN_TOKENS).filter((t) => t.length >= 2);
    if (span.length < MIN_SPAN_TOKENS) continue;
    const phrase = span.join(" ");
    if (seen.has(phrase)) continue;
    seen.add(phrase);
    out.push(phrase);
  }
  return out;
}

export interface ResolvedQuote {
  ref: string; // the source the span is quoted from, PROVEN by ktiv-tolerant containment
  phrase: string; // the verified (longest) sub-phrase, pointing-stripped
}

// The longest contiguous run of `phrase` tokens (>= MIN, at ANY offset) that the
// candidate's text contains verbatim, ktiv-tolerant - or "" if none. Scanning all
// offsets (not just prefixes) tolerates citation FRAMING the cue-extraction grabs
// before the real quote ("...שאמרו [במסכת מנחות] <quote>"). The >= MIN-token /
// MIN-char floor on a verbatim run is what keeps a coincidental match impossible.
function containedPhrase(hayKtiv: string, phrase: string): string {
  const toks = phrase.split(" ");
  for (let len = toks.length; len >= MIN_SPAN_TOKENS; len--) {
    for (let start = 0; start + len <= toks.length; start++) {
      const sub = toks.slice(start, start + len).join(" ");
      if (sub.length >= MIN_SPAN_CHARS && hayKtiv.includes(ktiv(sub))) return sub;
    }
  }
  return "";
}

// What `classify` returns for an acceptable candidate: its Hebrew text (for the
// containment proof) and a preference score (higher wins when several candidates
// verify the same span - the caller encodes category priority + proximity, so a
// quote lands on the PRIMARY source, e.g. the pasuk, not a later work that merely
// repeats the phrase). Returning null REJECTS the candidate (wrong category, the
// source itself, the base verse, out of taxonomy...).
export interface Classified {
  he: string;
  score: number;
}

// Resolve + verify. search / prefilter / classify are injected so this stays a
// pure, unit-testable orchestrator (no network, no taxonomy knowledge):
//   - prefilter(ref): CHEAP synchronous shape gate (e.g. "not a commentary"),
//     applied before the candidate cap so noise never crowds out the real source.
//   - classify(ref): async gate + scorer. null = reject. The containment PROOF
//     (ktiv-tolerant verbatim run) runs on classify's returned text, so a wrong
//     category or a coincidental-phrase twin is dropped, never linked.
// One best-scored verified ref per span; deduped across spans.
export async function resolveQuotedRefs(opts: {
  sourceHe: string;
  search: (phrase: string) => Promise<string[]>;
  prefilter: (ref: string) => boolean;
  classify: (ref: string) => Promise<Classified | null>;
  maxSpans?: number;
  maxCandidates?: number;
}): Promise<ResolvedQuote[]> {
  const { sourceHe, search, prefilter, classify } = opts;
  const maxCandidates = opts.maxCandidates ?? 8;
  const spans = extractQuotedSpans(sourceHe).slice(0, opts.maxSpans ?? 6);

  // The genuine quotes in a discursive commentary are sparse and can sit LATE in
  // the text, so spans resolve CONCURRENTLY (the sefaria client caps real
  // parallelism) - sequential awaiting would make a long source unusably slow.
  const resolveOne = async (phrase: string): Promise<ResolvedQuote | null> => {
    const toks = phrase.split(" ");
    let hits: string[] = [];
    try {
      hits = await search(toks.slice(0, SEARCH_TOKENS).join(" ")); // query the CLEAN core
    } catch {
      hits = [];
    }
    const candidates = hits.filter(prefilter).slice(0, maxCandidates);
    const verified: Array<{ ref: string; phrase: string; len: number; score: number }> = [];
    for (const ref of candidates) {
      let c: Classified | null = null;
      try {
        c = await classify(ref);
      } catch {
        c = null;
      }
      if (!c) continue;
      const matched = containedPhrase(ktiv(stripHebrew(c.he)), phrase);
      if (matched) verified.push({ ref, phrase: matched, len: matched.split(" ").length, score: c.score });
    }
    if (!verified.length) return null;
    // Best by (score, match length): score carries category priority + proximity,
    // so an ambiguous phrase lands on the primary source in context.
    verified.sort((a, b) => b.score - a.score || b.len - a.len);
    return { ref: verified[0].ref, phrase: verified[0].phrase };
  };

  const perSpan = await Promise.all(spans.map(resolveOne));
  const seen = new Set<string>(); // dedup (two spans can quote the same source)
  const out: ResolvedQuote[] = [];
  for (const r of perSpan) {
    if (r && !seen.has(r.ref)) {
      seen.add(r.ref);
      out.push(r);
    }
  }
  return out;
}
